/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import type { EmotionalFeedData, EmotionalMessage, GetEmotionalFeedParams, Emotion } from './types';

// API response types (snake_case from backend)
type ApiEmotionalMessage = {
  id: string;
  conversation_id: string | null;
  content: string;
  user_first_name: string | null;
  user_avatar_url: string | null;
  emotion: Emotion;
  intensity: number;
  created_at: string;
};

type ApiEmotionalFeedData = {
  messages: ApiEmotionalMessage[];
};

type ApiEmotionalFeedResponse = {
  data: ApiEmotionalFeedData;
};

// Transform snake_case API response to camelCase for frontend
function transformEmotionalFeedResponse(apiData: ApiEmotionalFeedData): EmotionalFeedData {
  return {
    messages: apiData.messages.map(
      (msg): EmotionalMessage => ({
        id: msg.id,
        conversationId: msg.conversation_id,
        content: msg.content,
        userFirstName: msg.user_first_name,
        userAvatarUrl: msg.user_avatar_url,
        emotion: msg.emotion,
        intensity: msg.intensity,
        createdAt: msg.created_at,
      })
    ),
  };
}

export async function getEmotionalFeed(params: GetEmotionalFeedParams): Promise<EmotionalFeedData> {
  const { artistId, dateStart, dateEnd, platform } = params;

  if (!artistId) throw Error('Artist ID is required');
  if (!dateStart) throw Error('Start date is required');
  if (!dateEnd) throw Error('End date is required');

  const queryParams: Record<string, string> = {
    start_date: dateStart,
    end_date: dateEnd,
  };

  if (platform && platform.length > 0) {
    queryParams.platform = platform.join(',');
  }

  const url = `/web/studio/artists/${artistId}/conversations/analytics/emotional-feed`;
  const response = await api.executeGetRequest<ApiEmotionalFeedResponse>(url, queryParams);

  return transformEmotionalFeedResponse(response.data.data);
}

export function useEmotionalFeed(
  artistId: string,
  dateStart: string,
  dateEnd: string,
  platform?: import('./types').ConversationPlatform[]
) {
  const user = storage.getUser();

  return useQuery({
    enabled: !!artistId && !!user?.is_registered && !!dateStart && !!dateEnd,
    queryKey: queryKeys.artistEmotionalFeed(artistId, { dateStart, dateEnd, platform }),
    queryFn: () =>
      getEmotionalFeed({
        artistId,
        dateStart,
        dateEnd,
        platform,
      }),
    staleTime: 10_000, // Consider fresh for 10 seconds
    refetchInterval: 15_000, // Auto-refresh every 15 seconds
    refetchIntervalInBackground: false, // Only when tab is active
  });
}
